import { css, styled } from 'styled-components'

import { RoleStatus } from '@b-stock/account-api-client'
import {
  useAccessTokenRoles,
  useDecodedAccessToken,
} from '@b-stock/fe-core/bstock-next/components'
import { useFlag } from '@b-stock/fe-core/bstock-next/helpers'
import {
  BidStatus,
  getSelectedQuoteQuery,
  myLocationsQuery,
} from '@b-stock/fe-core/bstock-next/queries'
import { Breakpoints } from '@b-stock/fe-core/design-system'
import { ListingDtoListingTypeEnum } from '@b-stock/listing-api-client'
import { LocationAttribute } from '@b-stock/location-api-client'
import type { OrderResponseDto } from '@b-stock/order-api-client'

import { useBidFormContext } from '@/buyer-portal/components/features/listing/actions/auction/BidFormProvider'
import {
  AuctionState,
  BuyNowState,
} from '@/buyer-portal/components/features/listing/actions/auction/const'
import type { AuctionDetailState } from '@/buyer-portal/components/features/listing/actions/auction/useAuctionDetailState'
import FirstDibsBanner from '@/buyer-portal/components/features/listing/FirstDibsBanner'
import { useFirstDibsPurchase } from '@/buyer-portal/components/features/listing/hooks/useFirstDibsPurchase'
import { useIsSellerUserOwner } from '@/buyer-portal/components/features/listing/hooks/useIsSellerUserOwner'
import { BidActionCard } from '@/buyer-portal/components/features/listing/ListingActionColumn/actionCard'
import type { ExtendedOrchestratedAuctionListingDetails } from '@/buyer-portal/components/features/listing/ListingActionColumn/Bid/BidActions/types'
import { Payment } from '@/buyer-portal/components/features/listing/ListingActionColumn/Payment'
import { AvailableMethods } from '@/buyer-portal/components/features/listing/ListingActionColumn/Payment/AvailableMethods'
import { PaymentErrorBoundary } from '@/buyer-portal/components/features/listing/ListingActionColumn/Payment/PaymentErrorBoundary'
import useIsLoggedIn from '@/buyer-portal/helpers/useIsLoggedIn'
import { useIsServiceAccountOnly } from '@/buyer-portal/helpers/useIsServiceAccountOnly'
import { FeatureFlags } from '@/helpers/featureFlags'

import { ContractEstimates } from '../ContractEstimates/ContractEstimates'
import { ListingShippingSummary } from '../ListingShippingSummary'
import { ShippingInformationCard } from '../ShippingInformationCard'
import { useTrackActionColumnClick } from '../useTrackActionColumnClick'
import { AuctionEndedBanner } from './AuctionEndedBanner'
import { UnauthorizedBanner } from './UnauthorizedBanner'

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

const BidSection = styled.div<{ $enableLdpRedesign: boolean }>`
  display: flex;
  flex-direction: column;
  ${(o) =>
    !o.$enableLdpRedesign &&
    css`
      order: 2;

      @media ${Breakpoints.max.medium} {
        order: 1;
      }
    `}
`

const ShippingSummaryWrapper = styled.div<{ $enableLdpRedesign: boolean }>`
  ${(o) =>
    !o.$enableLdpRedesign &&
    css`
      order: 1;

      @media ${Breakpoints.max.medium} {
        order: 2;
      }
    `}

  &:empty {
    display: none;
  }
`

const PaymentWrapper = styled.div<{ $enableLdpRedesign: boolean }>`
  ${(o) =>
    !o.$enableLdpRedesign &&
    css`
      order: 3;

      @media ${Breakpoints.min.large} {
        margin-top: 2.5rem;
      }
    `}
`

export type AuctionActionColumnContentProps = {
  auctionDetailState: AuctionDetailState
  isVisibilityEnded: boolean
  listing: ExtendedOrchestratedAuctionListingDetails
  order?: OrderResponseDto | null
}

export const AuctionActionColumnContent = ({
  auctionDetailState,
  isVisibilityEnded,
  listing,
  order,
}: AuctionActionColumnContentProps) => {
  const roles = useAccessTokenRoles()
  const isActiveBuyer = roles?.buyer === RoleStatus.Active
  const decodedToken = useDecodedAccessToken({ required: false })
  const accountId = decodedToken?.accountId

  const myLocations = myLocationsQuery.useWithOpts(
    [
      {
        attributes: [LocationAttribute.DefaultDeliverable],
        ...(accountId && { accountId: [accountId] }),
      },
    ],
    { enabled: isActiveBuyer }
  )
  const defaultLocation = myLocations.data?.locations[0]

  const { data: selectedQuote } = getSelectedQuoteQuery.useWithOpts(
    [listing._id, accountId],
    {
      enabled:
        isActiveBuyer && (!!defaultLocation?.id || myLocations.isFetched),
    }
  )

  const { trackButtonClick } = useTrackActionColumnClick({
    data: listing,
    selectedQuote,
    winningState: auctionDetailState.bidState.toLowerCase(),
  })

  const isLoggedIn = useIsLoggedIn()
  const isSellerUserOwner = useIsSellerUserOwner(listing)
  const isServiceAccountOnly = useIsServiceAccountOnly()

  const auctionEnded = auctionDetailState.auctionState === AuctionState.Ended
  const buyerWon = auctionDetailState.bidState === BidStatus.WON

  const enableLdpRedesign = useFlag(FeatureFlags.ENABLE_LDP_REDESIGN)

  const { fdActive, earlyPrice: fdEarlyPrice } = useFirstDibsPurchase(listing)

  const showQuoteInformation =
    listing.listingType === ListingDtoListingTypeEnum.Contract &&
    !(isSellerUserOwner || isServiceAccountOnly) &&
    (!auctionEnded || buyerWon)

  // Payment logic
  const {
    details,
    editDeliveryMethod,
    autoQuoteGeneration,
    shippingQuotePending,
  } = useBidFormContext()
  const { additionalCharges, isWithinBuyNowWindow } = details

  // for contracts, we don't now show exact payment details (it's just estimates)
  const isAvailableMethodsShown =
    isLoggedIn &&
    !isSellerUserOwner &&
    listing.listingType !== ListingDtoListingTypeEnum.Contract
  // The Available Payment Methods section previews which methods will be
  // offered once the auction goes live, so — unlike the rest of the payment
  // block — it doesn't wait on this auction-state clause.
  const isPaymentShown =
    isAvailableMethodsShown &&
    // The bid card opens early during First Dibs, so payment shows too.
    (fdActive ||
      auctionDetailState.auctionState === AuctionState.Active ||
      auctionDetailState.buyNowState === BuyNowState.Active ||
      auctionDetailState.bidState === BidStatus.WON)

  return (
    <>
      <UnauthorizedBanner trackButtonClick={trackButtonClick} />
      <Body>
        <BidSection $enableLdpRedesign={enableLdpRedesign}>
          <BidActionCard
            auctionDetailState={auctionDetailState}
            listing={listing}
            isVisibilityEnded={isVisibilityEnded}
            order={order}
            topBanner={fdActive ? <FirstDibsBanner /> : undefined}
          />
        </BidSection>
        <ShippingSummaryWrapper $enableLdpRedesign={enableLdpRedesign}>
          {enableLdpRedesign ? (
            <ShippingInformationCard
              auctionOrOfferingEnded={auctionEnded}
              buyerWon={buyerWon}
              order={order}
              selectedQuote={selectedQuote}
              trackButtonClick={trackButtonClick}
              listing={listing}
            />
          ) : (
            <ListingShippingSummary
              auctionAutoQuoteGeneration={autoQuoteGeneration}
              auctionOrOfferingEnded={auctionEnded}
              buyerWon={buyerWon}
              order={order}
              selectedQuote={selectedQuote}
              trackButtonClick={trackButtonClick}
              listing={listing}
              renderResponsiveView={isPaymentShown || !!listing.contract}
            />
          )}
        </ShippingSummaryWrapper>
        {(enableLdpRedesign ? isAvailableMethodsShown : isPaymentShown) && (
          <PaymentWrapper $enableLdpRedesign={enableLdpRedesign}>
            <PaymentErrorBoundary listingId={listing._id}>
              {enableLdpRedesign ? (
                <AvailableMethods
                  isCreditCardEnabled={listing.isCreditCardEnabled}
                  currency={listing.currency}
                  listing={listing}
                  auctionOrOfferingEnded={auctionEnded}
                  buyerWon={buyerWon}
                />
              ) : (
                <Payment
                  loading={shippingQuotePending}
                  editDeliveryMethod={editDeliveryMethod}
                  additionalCharges={additionalCharges}
                  selectedQuote={selectedQuote}
                  basePrice={additionalCharges.basis}
                  buyNowPrice={fdEarlyPrice ?? listing.auction.buyNow?.price}
                  isWithinBuyNowWindow={isWithinBuyNowWindow}
                  shipping={listing.shipping}
                  currentListing={listing}
                  currency={listing.currency}
                  // BidActionCard's TlcBreakdown above already shows this
                  // listing's additional-charges total, so this block would
                  // otherwise repeat it in a second, disconnected card.
                  hideAdditionalCharges
                />
              )}
            </PaymentErrorBoundary>
          </PaymentWrapper>
        )}
      </Body>
      {isVisibilityEnded && (
        <AuctionEndedBanner bidStatus={auctionDetailState.bidState} />
      )}
      {!!listing.contract && (
        <ContractEstimates
          listing={listing}
          selectedQuote={selectedQuote}
          showQuoteInformation={showQuoteInformation}
        />
      )}
    </>
  )
}
